import React, { useEffect, useState } from 'react';
import InputWithPlaceholder from '../input-with-placeholder/input-with-placeholder';
import AutoComplete from '../../../common/autocomplete/autocomplete';
import { OSBUseLocation } from '../../owners-osb/common/osb-use-location/osb-use-location';
import PrimaryButton from '../../../common/primary-button/primary-button';
import {
    getFromSessionStorage,
    getObjectFromAEMJson,
    getVehiclePreferredDealer,
    isAuthenticatedFlow,
} from '../../owners-osb/osb-utils';
import {
    PRICE_CALCULATOR_ANALYTICS,
    PRICE_CALCULATOR_CONSTANTS,
} from '../price-calculator-constant';
import { ContentElements } from '../../../../models/priceCalculator-content-details';
import {
    getLocation,
    validateLocation,
    validateMileage,
    validateVinRegNo,
} from './service-price-calculator-search';
import './service-price-calculator-mobile-search.scss';
import AuthenticationService from '../../../../services/authentication-service/authentication-service';
import VehicleDropdown from '../vehicle-dropdown/vehicle-dropdown';
import { ProfileWithVehiclesResponse } from '../../../../models/profile-with-vehicles';
import {
    useDealerStep,
    useOSBStep,
    useVehicleStep,
} from '../../../../hooks/owners-osb';
import errorWarning from '../../../../assets/error-warning.svg';
import useMarketDealerInfoService from '../../../../hooks/owners-osb/use-market-dealerInfo-service';
import { useLocation } from 'react-router-dom';
import { triggerUserInputAnalytics } from '../analytics/price-calculator-analytics';
import { useAnalytics } from '../../../../hooks/use-analytics';
import { sanitizeMileage } from '../pc-utils';
interface Props {
    priceCalculatorContent: ContentElements[];
    priceCalculatorResultContent: ContentElements[];
    profile?: ProfileWithVehiclesResponse | undefined;
    findMyVinContent: ContentElements[];
    getVinSelectionType: (type: string) => void;
    getQuoteHandler: (
        vinInput: string,
        mileageInput: string,
        locationInput: string,
        isLocationDisabled: boolean
    ) => void;
}

const ServicePriceCalculatorMobileSearch = (props: Props) => {
    const [fireEvents] = useAnalytics();
    const { osbStep } = useOSBStep();
    const { osbDealerStep } = useDealerStep();
    const authService = new AuthenticationService();
    const { osbVehicleStep, setOSBVehicleStepPayload } = useVehicleStep();
    const [vinRegError, setVinRegError] = useState('');
    const [formattedMileage, setFormattedMileage] = useState<string>(
        osbVehicleStep.vinMileage
    );
    const [mileageError, setMileageError] = useState('');
    const [locationInput, setLocationInput] = useState<string>('');
    const [locationError, setLocationError] = useState<string>('');
    const [vinSelectionType, setVinSelectionType] = useState<string>(
        getFromSessionStorage('pc-manual-vin') === 'false'
            ? 'DROPDOWN_VIN_AUTH'
            : 'INPUT_VIN'
    );
    const locatonFieldDisbledMessage = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.LOCATION_FIELD_DISABLED_MESSAGE,
        props.priceCalculatorResultContent
    );
    const [showLocationLabel, setshowLocationLabel] = useState<boolean>(false);
    const locationLabelShow = (flag: boolean) => {
        setshowLocationLabel(flag);
    };
    const [vinRegInput, setVinRegInput] = useState<string>(
        osbVehicleStep.vinRegNo || props.profile?.vehicles?.[0]?.vin || ''
    );
    const { fetchMarketDealerInfo } = useMarketDealerInfoService();
    const [mileageInput, setMileageInput] = useState<string>(
        osbVehicleStep.vinMileage || ''
    );
    const [preferredDealerId, setPreferredDealerId] = useState<string>('');

    const deepLinkParams = new URLSearchParams(useLocation().search);
    async function getPreferredDealer(vinRegInput: string | number) {
        try {
            const results = await fetchMarketDealerInfo(deepLinkParams);
            return (
                (props.profile?.vehicles &&
                    getVehiclePreferredDealer(
                        props.profile?.vehicles,
                        vinRegInput,
                        osbStep.numberOfLeadingCharactersToStrip,
                        results?.dealers || [], // Access dealers from the resolved value
                        true
                    )) ||
                ''
            );
        } catch (error) {
            console.error(
                'An error occurred while fetching market dealer info:',
                error
            );
            return '';
        }
    }
    const [isLocationDisabled, setIsLocationDisabled] = useState<boolean>(
        preferredDealerId.length > 0
    );
    const handleVinRegChange = async (value: string) => {
        setOSBVehicleStepPayload({
            vinRegNo: value,
        });
        if (!value) return;
        if (value === 'enter_vin_manually') {
            setIsLocationDisabled(false);
            setOSBVehicleStepPayload({
                vinRegNo: '',
            });
            setPreferredDealerId('');
            return;
        }
        setVinRegInput(value);
        const error = validateVinRegNo(value, props.priceCalculatorContent);
        setVinRegError(error);
        if (error !== '') {
            return;
        }

        if (vinSelectionType === 'DROPDOWN_VIN_AUTH') {
            const preferredDealer = await getPreferredDealer(value);
            setPreferredDealerId(preferredDealer);
            setIsLocationDisabled(preferredDealer !== '');
        }
        triggerUserInputAnalytics(
            fireEvents,
            PRICE_CALCULATOR_ANALYTICS.VIN_LABEL
        );
    };

    const handleMileageChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        setMileageInput(value);
        const finalMileage = sanitizeMileage(value);
        setFormattedMileage(finalMileage);
        const error = validateMileage(value, props.priceCalculatorContent);
        if (error.trim() !== '') {
            setMileageError(error);
            return;
        } else {
            setMileageError('');
        }
        triggerUserInputAnalytics(
            fireEvents,
            PRICE_CALCULATOR_ANALYTICS.MILEAGE_LABEL
        );
    };

    const fetchLocationInput = (input: string) => {
        setLocationInput(input);
        if (input == '') {
            setLocationError('');
            return;
        }
        triggerUserInputAnalytics(
            fireEvents,
            PRICE_CALCULATOR_ANALYTICS.LOCATION_LABEL
        );
    };
    const getQuote = () => {
        const vinErr = validateVinRegNo(
            vinRegInput,
            props.priceCalculatorContent
        );
        const mileErr = validateMileage(
            mileageInput,
            props.priceCalculatorContent
        );
        const locationIn =
            locationInput.length > 0
                ? locationInput
                : osbDealerStep.selectedLocation;
        let locationErr;
        if (!isLocationDisabled) {
            locationErr = validateLocation(
                locationIn,
                props.priceCalculatorContent
            );
        } else {
            locationErr = '';
        }

        if (
            vinErr.trim().length > 0 ||
            mileErr.trim().length > 0 ||
            locationErr.trim().length > 0
        ) {
            setVinRegError(vinErr);
            setMileageError(mileErr);
            setLocationError(locationErr);
            return;
        }
        if (
            preferredDealerId.trim().length <= 0 &&
            locationIn.trim().length <= 0
        ) {
            return;
        }

        props.getQuoteHandler(
            vinRegInput,
            mileageInput,
            locationIn,
            isLocationDisabled
        );
    };

    const onVinSelectionChange = async (section: string) => {
        try {
            if (section === 'DROPDOWN_VIN_AUTH') {
                const preferredDealer = await getPreferredDealer(vinRegInput);
                setPreferredDealerId(preferredDealer);
                setLocationInput('');
                setIsLocationDisabled(preferredDealer !== '');
                setVinRegInput(
                    osbVehicleStep.vinRegNo ||
                        props.profile?.vehicles?.[0]?.vin ||
                        ''
                );
            } else {
                setIsLocationDisabled(preferredDealerId.length > 0);
            }
            setVinSelectionType(section);
            props.getVinSelectionType(section);
        } catch (error) {
            console.error(
                'An error occurred while fetching market dealer info:',
                error
            );
        }
    };

    const handleSignInClick = () => {
        authService.login();
    };

    useEffect(() => {
        const fetchPreferredDealer = async () => {
            let preferredDlr = '';
            if (vinSelectionType === 'DROPDOWN_VIN_AUTH') {
                try {
                    preferredDlr = await getPreferredDealer(vinRegInput);
                    setPreferredDealerId(preferredDlr);
                    setIsLocationDisabled(preferredDlr !== '');
                } catch (error) {
                    console.error('ERROR');
                }
            } else {
                setIsLocationDisabled(preferredDealerId.length > 0);
            }
        };
        fetchPreferredDealer();
    }, []);

    const subheader = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.PC_SUBHEADER,
        props.priceCalculatorContent
    );
    const subheaderParts = subheader.split('##');

    const myLocationLabel = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.My_Location_Label,
        props.priceCalculatorContent
    );
    const myLocationPlaceholder = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.My_Location_PlaceHolder,
        props.priceCalculatorContent
    );
    const searchHintLabel = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.Search_Hint_Label,
        props.priceCalculatorContent
    );
    const autocompleteSearchStringLength = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.Autocomplete_Search_String_Length,
        props.priceCalculatorContent
    );
    const findDealersByLocationLabel = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.FIND_DEALERS_BY_LOCATION_BUTTON_LABEL,
        props.priceCalculatorResultContent
    );
    const useMyLocationLabel = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.Use_My_Location_Label,
        props.priceCalculatorContent
    );
    const getAQuoteBtnLabel = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.Get_A_Quote_Btn_Label,
        props.priceCalculatorContent
    );
    const mileagePlaceholder = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.Mileage_PlaceHolder,
        props.priceCalculatorContent
    );
    const reviewHeader = getObjectFromAEMJson(
        PRICE_CALCULATOR_CONSTANTS.PC_Review_Header,
        props.priceCalculatorContent
    );

    return (
        <div className="spc-mobile-search-wrapper">
            <div className="spc-mobile-search-heading-container">
                <h1 className="spc-header" aria-label="spc-header">
                    {reviewHeader}
                </h1>
                <div className="spc-sub-header">
                    <span>
                        {subheaderParts[0]}
                        {!isAuthenticatedFlow && (
                            <>
                                {subheaderParts[1]}
                                <span
                                    className="sign-in"
                                    onClick={handleSignInClick}
                                >
                                    {subheaderParts[2]}
                                </span>
                                {subheaderParts[3]}
                            </>
                        )}
                    </span>
                </div>
            </div>
            <div className="spc-mobile-search-input-container">
                <div className="spc-mobile-search-input-vin-container">
                    <div className="spc-mobile-search-input-vin">
                        <VehicleDropdown
                            priceCalculatorContent={
                                props.priceCalculatorContent
                            }
                            vehicles={props.profile?.vehicles || []}
                            vinRegError={vinRegError}
                            onVehicleSelect={handleVinRegChange}
                            onVinSelectionChange={onVinSelectionChange}
                            findMyVinContent={props.findMyVinContent}
                        />
                    </div>
                </div>
                <div className="spc-mobile-search-input-mileage-container">
                    <InputWithPlaceholder
                        placeholder={mileagePlaceholder}
                        onChange={handleMileageChange}
                        error={mileageError}
                        formattedValue={formattedMileage}
                    />
                </div>
                <div className="spc-mobile-search-input-location-container">
                    <div
                        className={`spc-mobile-search-input-autocomplete ${
                            isLocationDisabled ? 'disabled' : ''
                        }`}
                    >
                        <div className="pc-input-label">
                            {showLocationLabel ||
                                (locationInput.length > 0 && (
                                    <span>{myLocationLabel}</span>
                                ))}
                            {locationError && (
                                <img
                                    className="osb-error-icon"
                                    src={errorWarning}
                                />
                            )}
                        </div>
                        <AutoComplete
                            name="myLocation"
                            id="myLocation"
                            google={true}
                            placeHolderText={myLocationPlaceholder}
                            text={osbDealerStep.selectedLocation}
                            message={searchHintLabel}
                            errorClass={`${
                                locationError ? 'pc-input-text-error' : ''
                            } `}
                            fetchAutoCompleteInput={fetchLocationInput}
                            searchStringLength={autocompleteSearchStringLength}
                            onBlur={() => locationLabelShow(false)}
                            onFocus={() => locationLabelShow(true)}
                        />
                        {locationError && (
                            <div className="spc-input-error">
                                {locationError}
                            </div>
                        )}
                    </div>
                    {isLocationDisabled && (
                        <>
                            <div
                                className="spc-enable-location-link"
                                onClick={() => {
                                    setIsLocationDisabled(false);
                                }}
                            >
                                {findDealersByLocationLabel}
                            </div>
                            {locatonFieldDisbledMessage && (
                                <div className="spc-preferred-dealer-message">
                                    {locatonFieldDisbledMessage}
                                </div>
                            )}
                        </>
                    )}
                    {!isLocationDisabled && (
                        <div className="spc-mobile-search-input-use-my-location">
                            <OSBUseLocation
                                showCurrentLocation={() => {
                                    getLocation(props.priceCalculatorContent)
                                        .then(locationData => {
                                            setLocationInput(
                                                locationData as string
                                            );
                                        })
                                        .catch(errorMsg => {
                                            setLocationError(errorMsg?.message);
                                        });
                                }}
                                useLocationLabel={useMyLocationLabel}
                            />
                        </div>
                    )}
                </div>
                <div className="spc-mobile-search-input-button-container">
                    <PrimaryButton
                        role="link"
                        color={'dark'}
                        fill={'fill'}
                        chevron={false}
                        aria-label="get-a-price-cta"
                        aria-labelledby="get a price cta"
                        onClick={getQuote}
                        dataTestId="get-a-price-cta"
                    >
                        {getAQuoteBtnLabel}
                    </PrimaryButton>
                </div>
            </div>
        </div>
    );
};

export default ServicePriceCalculatorMobileSearch;
